import tw from 'twin.macro'
import React, { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Combobox } from '@headlessui/react'
import * as Portal from '@radix-ui/react-portal'
import { CgImage } from 'react-icons/cg'

import { Spinner } from 'common/components/Spinner/Spinner'

import exerciseFallbackImg from 'modules/Programs/assets/exercise-fallback-img.svg'
import { SuggestionContainer, SuggestionItem } from 'common/components/Suggestions/Suggestions'
import { inputBase } from 'common/components/Input/Input'
import { usePopper } from 'common/hooks/usePopper'
import { getStandardizedName } from 'common/utils/stringUtils'

// Generic autocomplete picker for the post button's video/program/workout targets.
// Carried over from the announcements form. An item `isItemDisabled` says yes to is
// listed so the coach can see it exists, but shows `disabledLabel` in place of its
// thumbnail and cannot be chosen.
//
// With `listAllOnFocus`, the list opens as soon as the field is focused, before anything
// is typed, and is not cut off — so a coach can scroll to an item whose name they have
// forgotten. Typing still narrows it.
//
// `isLoading` says the list has not arrived yet, so an empty one reads "Loading..." rather
// than "No suggestions found." — which matters most for a picker that opens on focus,
// since it can be open before its list is there.
//
// `getItemSubtitle`, when given, adds a smaller line under each item's name, such as
// the week and day a workout falls on.
export default function SuggestionInput({
  inputRef: inputRefProp,
  label,
  name,
  value,
  selectedLabel,
  suggestions,
  setValue,
  clearErrors,
  error,
  getItemLabel,
  getPreviewImg,
  onSelect,
  isItemDisabled,
  disabledLabel,
  getItemSubtitle,
  listAllOnFocus = false,
  placeholder,
  isLoading = false,
}) {
  // The suggestions container is absolutely positioned and gets its width from
  // this measurement — without it the list renders zero-width (invisible sliver).
  // Measured in a layout effect: at first render the input isn't mounted yet,
  // and render-time measurement stays undefined until something re-renders.
  const localInputRef = useRef()
  const inputRef = inputRefProp || localInputRef
  const [inputWidth, setInputWidth] = useState(undefined)
  useLayoutEffect(() => {
    setInputWidth(inputRef.current?.getBoundingClientRect()?.width)
  }, [inputRef])
  const [searchText, setSearchText] = useState(selectedLabel)

  useEffect(() => {
    setSearchText(selectedLabel)
  }, [selectedLabel])

  let [popperTrigger, popperContainer] = usePopper({
    placement: 'bottom-start',
    strategy: 'fixed',
  })

  // The chosen item's name sitting in the field is not a search: reopening the list to
  // change the choice should show everything again.
  const filterText = listAllOnFocus && searchText === selectedLabel ? '' : searchText || ''

  const suggestedItems = suggestions
    ? Object.entries(suggestions).filter(
        ([_, data]) =>
          data &&
          typeof data === 'object' &&
          getStandardizedName(getItemLabel(data))?.includes(getStandardizedName(filterText))
      )
    : []

  return (
    <div className='mb-4'>
      <label className='capitalize inline-flex cursor-pointer font-semibold text-tBlack' tw='mb-1'>
        {label}
      </label>
      <Combobox
        value={value}
        onChange={(id) => {
          clearErrors(name)
          setValue(name, id || '', { shouldValidate: true })
          if (onSelect) {
            onSelect(id)
          }
        }}
        nullable
        immediate={listAllOnFocus}
      >
        <div ref={popperTrigger} className='w-full'>
          <Combobox.Input
            ref={inputRef}
            placeholder={placeholder || `Search ${label.toLowerCase()}s`}
            // The visible label above is not tied to this input, so without this the
            // field has no name for a screen reader to announce.
            aria-label={label}
            autoComplete='off'
            displayValue={() => searchText}
            onChange={(e) => {
              const text = e.target.value
              setSearchText(text)
              if (text === '' && value) {
                setValue(name, '', { shouldValidate: true })
                if (onSelect) {
                  onSelect('')
                }
              }
            }}
            onBlur={() => {
              if (searchText !== selectedLabel) {
                if (value) {
                  setValue(name, '', { shouldValidate: true })
                  if (onSelect) {
                    onSelect('')
                  }
                }
                setSearchText('')
              }
            }}
            className='text-ellipsis bg-inherit'
            css={[inputBase, error && tw`ring-tRed focus:ring-tRed`]}
          />
          {error && <p className='flex text-xs mt-1 text-tRed'>{error.message}</p>}
        </div>
        {(listAllOnFocus || searchText?.length > 0) && (
          <Portal.Root>
            <div style={{ width: inputWidth }} className='z-[999]' ref={popperContainer}>
              <Suggestions
                label={label}
                suggestions={suggestedItems}
                getItemLabel={getItemLabel}
                getPreviewImg={getPreviewImg}
                getItemSubtitle={getItemSubtitle}
                isLoading={isLoading}
                isItemDisabled={isItemDisabled}
                disabledLabel={disabledLabel}
                containerClasses='!top-2'
                maxSuggestions={listAllOnFocus ? Infinity : 30}
              />
            </div>
          </Portal.Root>
        )}
      </Combobox>
    </div>
  )
}

function Suggestions({
  label,
  suggestions,
  getItemLabel,
  getPreviewImg,
  getItemSubtitle,
  isItemDisabled,
  disabledLabel,
  containerClasses,
  maxSuggestions,
  isLoading,
}) {
  // Headless UI's default `modal` locks page scrolling while the list is open, and in the
  // reply dialog that lock threw the page back to the top on the first typed letter. The
  // list is portalled and fixed-position, so it does not need the lock.
  if (!suggestions?.length) {
    return (
      <Combobox.Options as={Fragment} modal={false}>
        <SuggestionContainer containerClasses={containerClasses ? containerClasses : '!top-9'}>
          <li className='cursor-default select-none py-2 px-2'>
            <EmptySuggestionsMessage isLoading={isLoading} />
          </li>
        </SuggestionContainer>
      </Combobox.Options>
    )
  }

  return (
    <Combobox.Options as={Fragment} modal={false}>
      <SuggestionContainer containerClasses={containerClasses ? containerClasses : '!top-9'}>
        {suggestions.slice(0, maxSuggestions).map(([itemId, item]) => {
          const previewImg = getPreviewImg ? getPreviewImg(item) : null
          const disabled = isItemDisabled ? isItemDisabled(item) : false
          const subtitle = getItemSubtitle ? getItemSubtitle(item) : ''

          return (
            <Combobox.Option as={Fragment} key={itemId} value={itemId} disabled={disabled}>
              {({ active }) => (
                <SuggestionItem active={active} $disabled={disabled} title={disabled ? disabledLabel : undefined}>
                  <div className='flex items-center'>
                    <div className='flex items-center flex-1'>
                      <div className='relative flex h-8 w-10 rounded overflow-hidden bg-tBlack'>
                        {disabled ? (
                          <div className='w-full h-full flex items-center justify-center'>
                            <Spinner
                              className='w-4 h-4 text-gray-100 opacity-50'
                              fillClassName='fill-tBlack opacity-50'
                            />
                          </div>
                        ) : previewImg ? (
                          <object data={previewImg} type='image/jpeg' className='w-full h-full object-cover'>
                            <img
                              src={exerciseFallbackImg}
                              title={`${label} thumbnail not available`}
                              alt={`${label} thumbnail not available`}
                              className='w-full h-full object-cover'
                              style={{
                                imageRendering: 'pixelated',
                              }}
                            />
                          </object>
                        ) : (
                          <CgImage className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-50 w-4 h-4' />
                        )}
                      </div>
                      <div className='ml-3 flex flex-col'>
                        <span className='text-tBlack font-medium text-sm capitalize'>
                          {getItemLabel(item) || 'untitled'}
                        </span>
                        {subtitle && <span className='text-xxs text-gray-400'>{subtitle}</span>}
                      </div>
                      {disabled && disabledLabel && (
                        <span className='ml-2 text-xs font-medium text-gray-500 normal-case'>{disabledLabel}</span>
                      )}
                    </div>
                  </div>
                </SuggestionItem>
              )}
            </Combobox.Option>
          )
        })}
      </SuggestionContainer>
    </Combobox.Options>
  )
}

export function EmptySuggestionsMessage({ isLoading }) {
  if (isLoading) {
    return (
      <div className='flex items-center'>
        <Spinner className='w-4 h-4 mr-2 text-gray-100' fillClassName='fill-gray-400' />
        Loading...
      </div>
    )
  }

  return <div>No suggestions found.</div>
}
